import { AxiosError } from 'axios'
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'
import {
  ApiResponse,
  Country,
  EpenseYearlyReportListSliceState,
  ExpenseYearlyReportListProps,
  MonthWiseExpenseData,
  Result,
  getMonthWiseExpenseListProps,
} from '../../../types/ExpenseManagement/ExpenseYearlyReport/expenseYearlyReportTypes'
import expenseYearlyReportApi from '../../../middleware/api/ExpenseManagement/ExpenseYearlyReport/expenseYearlyReportApi'

const getExpenseYearlyReport = createAsyncThunk(
  'assetManagement/expenseYearlyReport',
  async (props: ExpenseYearlyReportListProps, thunkApi) => {
    try {
      return await expenseYearlyReportApi.getExpenseYearlyReport(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getCountryThunk = createAsyncThunk(
  'assetManagement/getCountries',
  async (_, thunkApi) => {
    try {
      return await expenseYearlyReportApi.getCountryList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getMonthWiseExpenseThunk = createAsyncThunk(
  'assetManagement/monthWiseExopeseReport',
  async (props: getMonthWiseExpenseListProps, thunkApi) => {
    try {
      return await expenseYearlyReportApi.getMonthWiseExpenseList(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export const initialExpenseYearlyReportListState: EpenseYearlyReportListSliceState =
  {
    isLoading: ApiLoadingState.idle,
    getExpensesYearly: {} as ApiResponse,
    getCountriesData: {} as Country[],
    getMonthWiseExpenseData: {} as MonthWiseExpenseData[],
  }

const expenseYearlyReportSlice = createSlice({
  name: 'assetWarrantyReport',
  initialState: initialExpenseYearlyReportListState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getExpenseYearlyReport.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getExpenseYearlyReport.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getExpensesYearly = action.payload
      })

      .addCase(getCountryThunk.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getCountryThunk.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getCountriesData = action.payload
      })

      .addMatcher(isAnyOf(getMonthWiseExpenseThunk.pending), (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addMatcher(
        isAnyOf(getMonthWiseExpenseThunk.fulfilled),
        (state, action) => {
          state.isLoading = ApiLoadingState.succeeded
          state.getMonthWiseExpenseData = action.payload
        },
      )
  },
})

const expenseYearlyReportThunk = {
  getExpenseYearlyReport,
  getCountryThunk,
  getMonthWiseExpenseThunk,
}

function isLoading(state: RootState): LoadingState {
  return state.expenseYearlyReport.isLoading
}

const expenseYearlyReportResponse = (state: RootState): ApiResponse =>
  state.expenseYearlyReport.getExpensesYearly

const expenseYearlyReportResult = (state: RootState): Result[] =>
  state.expenseYearlyReport.getExpensesYearly.result

const getContrySelector = (state: RootState): Country[] =>
  state.expenseYearlyReport.getCountriesData

const getMonthWiseExpenseData = (state: RootState): MonthWiseExpenseData[] =>
  state.expenseYearlyReport.getMonthWiseExpenseData

export const expenseYearlyReportSelectors = {
  isLoading,
  expenseYearlyReportResponse,
  getContrySelector,
  getMonthWiseExpenseData,
  expenseYearlyReportResult,
}

export const expenseYearlyReportService = {
  ...expenseYearlyReportThunk,
  actions: expenseYearlyReportSlice.actions,
  selectors: expenseYearlyReportSelectors,
}

export default expenseYearlyReportSlice.reducer
